import { Button } from '@/components/ui/button'
import { COMPETITIONS_LIST, type CompetitionConfig } from '@/lib/competitions'
import { CompetitionCard } from './CompetitionCard'
import { getCurrentSeason, type LeagueFormData } from './useRegisterLeague'
import { ChevronLeft } from 'lucide-react'

interface StepCompetitionSelectProps {
  formData: LeagueFormData
  onUpdate: (updates: Partial<LeagueFormData>) => void
  onNext: () => void
  onBack: () => void
  loading?: boolean
  error?: string | null
}

export function StepCompetitionSelect({ formData, onUpdate, onNext, onBack, loading, error }: StepCompetitionSelectProps) {
  const selectedComp = COMPETITIONS_LIST.find(c => c.code === formData.competitionCode)
  const season = selectedComp ? getCurrentSeason(selectedComp) : ''

  const handleSelect = (comp: CompetitionConfig) => {
    const newSeason = getCurrentSeason(comp)
    onUpdate({ competitionCode: comp.code, season: newSeason })
  }

  const handleNext = () => {
    if (!formData.competitionCode) return
    onNext()
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Choose a competition</h2>
      <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
        Select the football league your group will be predicting.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-4
                      sm:grid-cols-2 sm:gap-3">
        {COMPETITIONS_LIST.map(comp => (
          <CompetitionCard
            key={comp.code}
            competition={comp}
            isSelected={formData.competitionCode === comp.code}
            onClick={() => handleSelect(comp)}
          />
        ))}
      </div>

      {/* Selected competition confirmation */}
      {selectedComp && (
        <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 mb-4">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            <span className="font-medium text-zinc-800 dark:text-zinc-200 inline-flex items-center gap-1.5">
              {selectedComp.logo ? (
                <img src={selectedComp.logo} alt="" className="w-4 h-4 object-contain inline-block" />
              ) : (
                <span>{selectedComp.flag}</span>
              )}
              {selectedComp.name}
            </span>
            {' '}&middot; Season: <span className="font-medium text-zinc-800 dark:text-zinc-200">{season}</span>
            {' '}&middot; {selectedComp.matchdays} matchdays
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-sm rounded-lg p-3 mb-4">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button onClick={handleNext} className="flex-1" disabled={!formData.competitionCode || loading}>
          {loading ? 'Creating...' : 'Continue'}
        </Button>
      </div>
    </div>
  )
}
